import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Space} from 'antd';
import {useTranslation} from 'react-i18next';
import 'react-quill/dist/quill.snow.css';
import {marked} from 'marked';
import parse from 'html-react-parser';
import TurndownService from 'turndown';
import ReactQuill from 'react-quill';
import {
  StyledPolicyEditorWithPreview,
  StyledPolicyEditorWithPreviewAlert,
  StyledPolicyEditorWithPreviewTitle
} from './StyledPolicyEditorWithPreview';
import EditButton from '../../../../EditButton';
import AnimatedSwitcher from '../../../../animations/AnimatedSwitcher';

const turndownService = new TurndownService();

const getMarkedValue = (value) => marked(value || '');

const PolicyEditorWithPreview = ({
  alert,
  description,
  defaultValue,
  empty,
  enableEdit = true,
  onSave,
  title,

  ...rest
}) => {
  const [t] = useTranslation('main');
  const [isEdit, setIsEdit] = useState(false);
  const [inputValue, setInputValue] = useState(getMarkedValue(defaultValue));

  const defaultInputValue = useMemo(() => defaultValue || '', [defaultValue]);

  useEffect(() => {
    if (inputValue !== defaultValue) {
      setInputValue(getMarkedValue(defaultValue));
    }
  }, [defaultValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = () => setIsEdit(true);

  const handleCancel = () => {
    setIsEdit(false);
    if (inputValue !== defaultValue) setInputValue(getMarkedValue(defaultValue));
  }

  const handleSave = () => {
    onSave?.({
      value: turndownService.turndown(inputValue),
      successCallback: () => setIsEdit(false)
    });
  }

  const content = defaultValue || empty || '';

  const items = [
    {
      key: 'content',
      component: parse(marked(content))
    },
    {
      key: 'edit',
      component: <ReactQuill theme='snow' value={inputValue} onChange={setInputValue} />
    }
  ];

  return (
    <StyledPolicyEditorWithPreview
      title={title}
      {...rest}
    >
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <Space direction='vertical' size='small' style={{ width: '100%' }}>
          <StyledPolicyEditorWithPreviewTitle>
            <h3>{title}</h3>
            {enableEdit ? (isEdit
              ? (
                <Space size='small'>
                  <Button
                    disabled={inputValue === defaultInputValue}
                    onClick={handleSave}
                    type='primary'
                    size='small'
                  >{t('save')}</Button>
                  <Button
                    onClick={handleCancel}
                    size='small'
                  >
                    {t('cancel')}
                  </Button>
                </Space>
              )
              : <EditButton onClick={handleEdit} />
            ) : null
            }
          </StyledPolicyEditorWithPreviewTitle>

          {alert && <StyledPolicyEditorWithPreviewAlert message={alert} type='warning' showIcon />}
        </Space>

        <AnimatedSwitcher items={items} displayedKey={isEdit ? 'edit' : 'content'} />
      </Space>
    </StyledPolicyEditorWithPreview>
  );
}

PolicyEditorWithPreview.propTypes = {
  enableEdit: PropTypes.bool
}

PolicyEditorWithPreview.propTypes = {
  description: PropTypes.string,
  defaultValue: PropTypes.string,
  onSave: PropTypes.func,
  title: PropTypes.string,
}

export default PolicyEditorWithPreview;
